/**
 * SanitizeContentCard — one-click admin tool that fixes data artifacts across
 * med cards, informed consents, procedures, problems & emergencies, and
 * management forms.
 *
 * Calls POST /api/admin/sanitize/run on the backend. The endpoint is
 * idempotent — re-running on already-clean data is a no-op.
 *
 * Shows two buttons:
 *   • "Preview Changes" (dryRun=true) — reports how many records would change
 *     without modifying anything
 *   • "Apply Sanitizer Now" — same call with dryRun=false, asks for confirm
 */
import React, { useState } from 'react';
import { Sparkles, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SanitizeContentCard({ adminToken }) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null);

  const run = async (dryRun) => {
    if (!dryRun) {
      const ok = window.confirm(
        "This will rewrite every med card, informed consent, procedure, problem/emergency, and management form to repair text artifacts (e.g. \"T ake\" → \"Take\", \"30 60 minutes\" → \"30–60 minutes\", reformat consent signature blocks).\n\nIt's safe to re-run anytime. Proceed?"
      );
      if (!ok) return;
    }
    setBusy(true);
    setErr(null);
    setReport(null);
    try {
      const res = await fetch(`${API}/api/admin/sanitize/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ dryRun }),
      });
      if (!res.ok) throw new Error(`Sanitizer failed: ${res.status} ${res.statusText}`);
      const data = await res.json();
      setReport(data);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-sanitize-card"
      className="bg-gradient-to-br from-violet-50 to-purple-50 border-2 border-violet-200 rounded-xl p-5 space-y-4"
    >
      <div className="flex items-start gap-3">
        <div className="bg-violet-600 text-white rounded-lg p-2.5">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-base font-extrabold text-violet-900">Sanitize Content</h3>
          <p className="text-xs text-violet-800 mt-0.5 max-w-2xl leading-snug">
            Fixes text artifacts across all dental content records: lone-letter words
            (<code className="bg-white px-1 rounded">T ake</code> → <code className="bg-white px-1 rounded">Take</code>),
            numeric ranges (<code className="bg-white px-1 rounded">30 60 minutes</code> → <code className="bg-white px-1 rounded">30–60 minutes</code>),
            merged headings, and awkwardly wrapped consent signature blocks.
            <b className="text-violet-900"> Safe to re-run anytime — idempotent.</b>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => run(true)}
          disabled={busy}
          className="bg-white hover:bg-violet-50 border-2 border-violet-300 text-violet-800 px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold disabled:opacity-50"
          data-testid="sanitize-preview-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
          Preview Changes (Dry Run)
        </button>
        <button
          onClick={() => run(false)}
          disabled={busy}
          className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold disabled:opacity-50"
          data-testid="sanitize-apply-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          Apply Sanitizer Now
        </button>
      </div>

      {err && (
        <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded-lg p-3" data-testid="sanitize-error">
          {err}
        </div>
      )}

      {report && (
        <div className="bg-white border border-violet-200 rounded-lg p-3 text-sm" data-testid="sanitize-report">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <p className="font-extrabold text-slate-900">
              {report.dryRun ? 'Dry run complete — nothing was changed' : 'Sanitizer applied'}
            </p>
          </div>
          <p className="text-xs text-slate-600 mb-2">
            <b>{report.totalDocsTouched}</b> record(s) touched ·{' '}
            <b>{report.totalFieldsChanged}</b> field(s) {report.dryRun ? 'would change' : 'updated'}
          </p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-1">Source</th>
                <th className="py-1 text-right">Total</th>
                <th className="py-1 text-right">Touched</th>
                <th className="py-1 text-right">Fields</th>
              </tr>
            </thead>
            <tbody className="text-slate-800">
              <tr className="border-t border-slate-100">
                <td className="py-1">med_cards_with_content.json (file)</td>
                <td className="py-1 text-right">—</td>
                <td className="py-1 text-right">{report.medCardsJson?.changed > 0 ? 1 : 0}</td>
                <td className="py-1 text-right font-bold">{report.medCardsJson?.changed ?? 0}</td>
              </tr>
              {(report.collections || []).map((c) => (
                <tr key={c.collection} className="border-t border-slate-100">
                  <td className="py-1">{c.collection}</td>
                  <td className="py-1 text-right">{c.total}</td>
                  <td className="py-1 text-right">{c.docsTouched}</td>
                  <td className="py-1 text-right font-bold">{c.fieldsChanged}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
